import { useEffect, useState, type FormEvent, type ReactNode } from 'react'
import { useFetch, useLazyFetch } from '@/hooks/useApi'
import { useConfigQuery } from '@/context/ClubContext'
import {
  AsyncState,
  Button,
  EmptyState,
  Field,
  Input,
  Kpi,
  PageHead,
  SectionHead
} from '@/components/ui'
import { ClubGate } from '@/components/ClubGate'
import {
  money,
  count,
  share,
  duration,
  dateTime,
  paymentLabel,
  dateInputToMs,
  msToDateInput
} from '@/lib/format'
import { Cur, Money } from '@/lib/currency'

const DAY_MS = 24 * 60 * 60 * 1000

// Локальные контракты ответов отчётов (зеркало @billiard/shared — не импортируем).

/** Строка списка смен (GET /v1/reports/shifts). */
interface ShiftListRow {
  id: string
  number: number
  openedAt: number
  closedAt: number | null
  openedByName: string | null
  revenue: number
  encashment: number
  cashTotal: number
  cardTotal: number
}

/** Сводка по смене + разбивки (GET /v1/reports/shifts/:id). */
interface ShiftDetailRow {
  id: string
  number: number
  openedAt: number
  closedAt: number | null
  openedByUserId: string | null
  closedByUserId: string | null
  revenue: number
  openingCash: number
  cashTotal: number
  cardTotal: number
  encashment: number
}

interface ShiftSessionRow {
  id: string
  tableId: string | null
  tableName: string
  tariffId: string | null
  tariffName: string
  startedAt: number
  endedAt: number | null
  durationMin: number
  amount: number
  cashAmount: number
  cardAmount: number
  paymentMethod: string | null
  plannedMinutes: number | null
  discountAmount: number
  bonusUsed: number
}

interface ShiftBreakdownRow {
  id: string
  name: string
  count: number
  amount: number
}

interface ShiftAggregates {
  count: number
  revenue: number
  cash: number
  card: number
  totalDiscount: number
  totalBonusUsed: number
}

interface ShiftDetail {
  shift: ShiftDetailRow
  openedByName: string | null
  closedByName: string | null
  sessions: ShiftSessionRow[]
  totals: ShiftAggregates
  byTariff: ShiftBreakdownRow[]
  byTable: ShiftBreakdownRow[]
}

export function Shifts(): React.JSX.Element {
  return (
    <>
      <PageHead
        eyebrow="Отчёты"
        title="Смены"
        lead="Список смен клуба за период. Откройте смену, чтобы увидеть Z-отчёт: итоги, разбивки и сессии."
      />
      <ClubGate>
        <ShiftsList />
      </ClubGate>
    </>
  )
}

function ShiftsList(): React.JSX.Element {
  const query = useConfigQuery()

  const now = Date.now()
  const [fromStr, setFromStr] = useState(() => msToDateInput(now - 30 * DAY_MS))
  const [toStr, setToStr] = useState(() => msToDateInput(now))
  const [period, setPeriod] = useState<{ from: number; to: number } | null>(null)
  const [selectedId, setSelectedId] = useState<string | null>(null)

  // from/to передаём только когда период применён (иначе — все смены клуба).
  const list = useFetch<ShiftListRow[]>(
    '/v1/reports/shifts',
    period ? { ...query, from: period.from, to: period.to } : query,
    [query?.clubId, period?.from, period?.to]
  )

  const onApply = (e: FormEvent): void => {
    e.preventDefault()
    const from = dateInputToMs(fromStr)
    // конец дня «по» включительно
    const to = dateInputToMs(toStr) + DAY_MS - 1
    setSelectedId(null)
    setPeriod({ from, to })
  }

  const onReset = (): void => {
    setSelectedId(null)
    setPeriod(null)
  }

  const rows = list.data ?? []

  return (
    <>
      <form className="toolbar" onSubmit={onApply}>
        <div className="toolbar__left">
          <Field label="С даты" htmlFor="sh-from">
            <Input
              id="sh-from"
              type="date"
              value={fromStr}
              max={toStr}
              onChange={(e) => setFromStr(e.target.value)}
            />
          </Field>
          <Field label="По дату" htmlFor="sh-to">
            <Input
              id="sh-to"
              type="date"
              value={toStr}
              min={fromStr}
              onChange={(e) => setToStr(e.target.value)}
            />
          </Field>
        </div>
        <div className="toolbar__right">
          {period && (
            <Button variant="ghost" size="sm" type="button" onClick={onReset}>
              Сбросить
            </Button>
          )}
          <Button variant="primary" size="sm" type="submit" disabled={list.loading}>
            {list.loading ? <span className="spin" /> : null}
            Применить
          </Button>
        </div>
      </form>

      <AsyncState
        query={list}
        isEmpty={rows.length === 0}
        empty="Смен за выбранный период нет."
        loadingLabel="Загружаем смены…"
      />

      {rows.length > 0 && (
        <div className="table-wrap">
          <table className="rtable rtable--clickable">
            <thead>
              <tr>
                <th className="num">№</th>
                <th>Открыта</th>
                <th>Закрыта</th>
                <th>Оператор</th>
                <th className="num">Выручка</th>
                <th className="num">Инкассация</th>
                <th className="num">Нал</th>
                <th className="num">Безнал</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((s) => (
                <tr
                  key={s.id}
                  className={s.id === selectedId ? 'is-selected' : undefined}
                  onClick={() => setSelectedId((id) => (id === s.id ? null : s.id))}
                >
                  <td className="num">{s.number}</td>
                  <td>{dateTime(s.openedAt)}</td>
                  <td>{dateTime(s.closedAt)}</td>
                  <td>{s.openedByName ?? '—'}</td>
                  <td className="num">
                    {money(s.revenue)} <Cur />
                  </td>
                  <td className="num">
                    {money(s.encashment)} <Cur />
                  </td>
                  <td className="num">
                    {money(s.cashTotal)} <Cur />
                  </td>
                  <td className="num">
                    {money(s.cardTotal)} <Cur />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selectedId && <ShiftDetailPanel key={selectedId} shiftId={selectedId} query={query} />}
    </>
  )
}

function ShiftDetailPanel({
  shiftId,
  query
}: {
  shiftId: string
  query: ReturnType<typeof useConfigQuery>
}): React.JSX.Element {
  const detail = useLazyFetch<ShiftDetail>()
  const { run } = detail

  // Загружаем детализацию при монтировании / смене выбранной смены.
  useEffect(() => {
    void run(`/v1/reports/shifts/${encodeURIComponent(shiftId)}`, query)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shiftId])

  return (
    <div className="card" style={{ marginTop: 18 }}>
      <AsyncState query={detail} loadingLabel="Загружаем смену…" />
      {detail.data && <ShiftView detail={detail.data} />}
    </div>
  )
}

function ShiftView({ detail }: { detail: ShiftDetail }): React.JSX.Element {
  const { shift, totals, sessions, byTariff, byTable, openedByName, closedByName } = detail
  const isOpen = shift.closedAt == null

  return (
    <>
      <SectionHead
        title={`Смена №${shift.number}`}
        meta={
          isOpen ? (
            <span className="pill pill--open">Открыта · X-отчёт</span>
          ) : (
            <span className="pill pill--closed">Закрыта · Z-отчёт</span>
          )
        }
      />

      <div className="kv-grid">
        <KV k="Открыта" v={dateTime(shift.openedAt)} />
        <KV k="Закрыта" v={dateTime(shift.closedAt)} />
        <KV k="Открыл" v={openedByName ?? '—'} />
        <KV k="Закрыл" v={closedByName ?? '—'} />
        <KV k="Касса на начало" v={<Money value={shift.openingCash} />} />
        <KV k="Инкассация" v={<Money value={shift.encashment} />} />
      </div>

      <SectionHead title="Итоги смены" />
      <div className="kpi-grid">
        <Kpi label="Выручка" value={<Money value={totals.revenue} />} gold />
        <Kpi label="Наличные" value={<Money value={totals.cash} />} />
        <Kpi label="Безналичные" value={<Money value={totals.card} />} />
        <Kpi label="Сессий" value={count(totals.count)} />
        <Kpi label="Скидки" value={<Money value={totals.totalDiscount} />} />
        <Kpi label="Бонусы списано" value={<Money value={totals.totalBonusUsed} />} />
      </div>

      <div className="grid" style={{ marginTop: 20 }}>
        <ShiftBreakdown title="По тарифам" rows={byTariff} />
        <ShiftBreakdown title="По столам" rows={byTable} />
      </div>

      <SectionHead title="Сессии" meta={`${count(sessions.length)} шт.`} />
      {sessions.length === 0 ? (
        <EmptyState message="В смене нет завершённых сессий." />
      ) : (
        <div className="table-wrap">
          <table className="rtable">
            <thead>
              <tr>
                <th>Стол</th>
                <th>Тариф</th>
                <th>Начало</th>
                <th className="num">Длит.</th>
                <th className="num">Сумма</th>
                <th>Оплата</th>
              </tr>
            </thead>
            <tbody>
              {sessions.map((s) => (
                <tr key={s.id}>
                  <td>{s.tableName}</td>
                  <td>{s.tariffName}</td>
                  <td>{dateTime(s.startedAt)}</td>
                  <td className="num">{duration(s.durationMin)}</td>
                  <td className="num">
                    {money(s.amount)} <Cur />
                  </td>
                  <td>{paymentLabel(s.paymentMethod)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  )
}

function ShiftBreakdown({
  title,
  rows
}: {
  title: string
  rows: ShiftBreakdownRow[]
}): React.JSX.Element {
  const total = rows.reduce((a, r) => a + r.amount, 0)
  return (
    <div>
      <SectionHead title={title} meta={`${count(rows.length)} поз.`} />
      {rows.length === 0 ? (
        <EmptyState message="Нет данных." />
      ) : (
        <div className="table-wrap">
          <table className="rtable">
            <thead>
              <tr>
                <th>Наименование</th>
                <th className="num">Кол-во</th>
                <th className="num">Сумма</th>
                <th className="num">Доля</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td>{r.name}</td>
                  <td className="num">{count(r.count)}</td>
                  <td className="num">
                    {money(r.amount)} <Cur />
                  </td>
                  <td className="num">{share(r.amount, total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function KV({ k, v }: { k: string; v: ReactNode }): React.JSX.Element {
  return (
    <div className="kv">
      <span className="kv__k">{k}</span>
      <span className="kv__v">{v}</span>
    </div>
  )
}
